import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { gql, useMutation, useQuery } from "@apollo/client";
import MuxPlayer from "@mux/mux-video-react";
import { HelpCircle, Play } from "lucide-react";
import BoundingBox from "../../components/video/BoundingBox";
import PawLoader from "../../components/PawLoader";
import "./GrabBitePlayer.css";
import CountdownTimer from "./CountdownTimer";
const ClickSound = process.env.PUBLIC_URL + "/assets/audio/click.mp3";

const RECORD_CLICK = gql`
  mutation RecordClick($muxPlaybackId: String!, $videoTimestamp: Float!) {
    recordClick(muxPlaybackId: $muxPlaybackId, videoTimestamp: $videoTimestamp)
  }
`;

const VIDEOS_BY_GROUP_QUERY = gql`
  query VideosByGroup {
    videosByGroup {
      muxPlaybackId
      coordinates
    }
  }
`;

const VIDEO_VIEW = gql`
  mutation VideoView($muxPlaybackId: String!) {
    videoView(muxPlaybackId: $muxPlaybackId) {
      success
      message
      viewedAt
    }
  }
`;

const COMPLETE_VIEW = gql`
  mutation CompleteView($muxPlaybackId: String!) {
    completeView(muxPlaybackId: $muxPlaybackId)
  }
`;

export default function GrabBitePlayer() {
	const videoRef = useRef(null);
	const clickAudioRef = useRef(null);
	const [videoRect, setVideoRect] = useState(null);
	const [showBoundingBox, setShowBoundingBox] = useState(true);
	const [isPlaying, setIsPlaying] = useState(false);
	const [viewRecorded, setViewRecorded] = useState(false);
	const [grabBiteTimes, setGrabBiteTimes] = useState([]);
	const [isGlowing, setIsGlowing] = useState(false);
	const [showModal, setShowModal] = useState(false);
	const [showTimeoutModal, setShowTimeoutModal] = useState(false);
	const [showTutorial, setShowTutorial] = useState(false);

	const navigate = useNavigate();

	const {
		data: videosData,
		loading: videosLoading,
		error: videosError,
		refetch,
	} = useQuery(VIDEOS_BY_GROUP_QUERY, {
		fetchPolicy: "network-only",
		nextFetchPolicy: "cache-first",
		notifyOnNetworkStatusChange: true,
	});
	// console.log("videosData:", videosData, "videosError:", videosError);

	const [videoView] = useMutation(VIDEO_VIEW);
	const [recordClick, { loading }] = useMutation(RECORD_CLICK);
	const [completeView] = useMutation(COMPLETE_VIEW);

	// console.log("videosData:", videosData, "videosError:", videosError);

	const playbackId = videosData?.videosByGroup?.muxPlaybackId || null;

	useEffect(() => {
		if (playbackId) {
			setShowBoundingBox(true); 
			updateRect(); 
		}
	}, [playbackId]);

	useEffect(() => {
		console.log("Loading audio from URL:", ClickSound);
		const el = new Audio(ClickSound);
		el.preload = "auto";
		
		// Add error handling for audio loading
		el.addEventListener('error', (e) => {
			console.error("Audio loading error:", e, "URL:", ClickSound);
		});
		
		el.addEventListener('canplaythrough', () => {
			console.log("Audio loaded successfully:", ClickSound);
		});
		
		clickAudioRef.current = el;

		const unlock = () => {
		const a = clickAudioRef.current;
		if (!a) return;
		a.play()
			.then(() => {
			a.pause();
			a.currentTime = 0;
			window.removeEventListener("pointerdown", unlock);
			window.removeEventListener("keydown", unlock);
			})
			.catch((err) => {
				console.error("Audio unlock failed:", err);
			});
		};
	
		window.addEventListener("pointerdown", unlock);
		window.addEventListener("keydown", unlock);
	
		return () => {
		window.removeEventListener("pointerdown", unlock);
		window.removeEventListener("keydown", unlock);
		};
	}, []); 
  
	useLayoutEffect(() => {
		window.addEventListener("resize", updateRect);
		const vidEl = videoRef.current;
		if (vidEl) vidEl.addEventListener("loadedmetadata", updateRect);
		return () => {
			window.removeEventListener("resize", updateRect);
			if (vidEl) vidEl.removeEventListener("loadedmetadata", updateRect);
		};
	}, [videoRect]);

	useEffect(() => {
		let timeoutId;
		if (showTutorial) {
			timeoutId = setTimeout(() => {
				setShowTutorial(false);
				setShowTimeoutModal(true);
			}, 15000);
		}
		return () => clearTimeout(timeoutId);
	}, [showTutorial]);

	function updateRect() {
		if (!videoRef.current) return;
		const vidEl = videoRef.current;
		const cw = vidEl.clientWidth,
			ch = vidEl.clientHeight,
			vw = vidEl.videoWidth,
			vh = vidEl.videoHeight;
		const cr = cw / ch,
			vr = vw / vh;
		if (cr < vr) {
			const nh = cw / vr;
			setVideoRect({
				left: 0,
				top: (ch - nh) / 2,
				width: cw,
				height: nh,
			});
		} else {
			const nw = ch * vr;
			setVideoRect({
				left: (cw - nw) / 2,
				top: 0,
				width: nw,
				height: ch,
			});
		}
	}

	const togglePlayPause = () => {
		const vid = videoRef.current;
		if (!vid) return;
		if (vid.paused) {
			setIsPlaying(true);
			setShowBoundingBox(true);
			setTimeout(() => {
				vid.play();
				setTimeout(() => setShowBoundingBox(false), 1000);
				if (!viewRecorded && playbackId) {
					setTimeout(async () => {
						try {
							await videoView({ variables: { muxPlaybackId: playbackId } });
							setViewRecorded(true);
						} catch (err) {
							console.error(err);
						}
					}, 5000);
				}
			}, 3000);
		} else {
			vid.pause();
			setIsPlaying(false);
		}
	};

	const handleGrabBiteClick = async (e) => {
		e.preventDefault();
		if (loading) return;

		const a = clickAudioRef.current;
		if (a) {
			try {
				// Check if audio is ready to play
				if (a.readyState >= 2) { // HAVE_CURRENT_DATA or higher
					a.currentTime = 0;      
					await a.play();         
				} else {
					console.warn("Audio not ready, attempting to play anyway");
					a.currentTime = 0;
					await a.play();
				}
			} catch (err) {
				console.error("Audio play failed:", err, "URL:", ClickSound);
			}
		} else {
			console.warn("Click audio not ready:", ClickSound);
		}


		if (videoRef.current) {
			const timestamp = parseFloat(videoRef.current.currentTime.toFixed(3));
			setGrabBiteTimes([...grabBiteTimes, timestamp]);
			try {
				await recordClick({
					variables: {
						muxPlaybackId: playbackId,
						videoTimestamp: timestamp,
					},
				});
				setIsGlowing(true);
				setTimeout(() => setIsGlowing(false), 500);
			} catch (error) {
				console.error(error);
			}
		}
	};

	const handlePlayNext = () => {
		setShowModal(false);
		setShowTimeoutModal(false);
		setIsPlaying(false);
		refetch();
	};

	if (videosLoading)
		return (
			<div className="w-full h-full flex items-center justify-center">
				<PawLoader />
			</div>
		);

	const coordsObj = videosData?.videosByGroup?.coordinates || {};
	const box = coordsObj.bounding_box;
	const instruction = coordsObj.markings;
	const promptText = instruction ? `Watch this ${instruction} puppy!` : "";

	const hasValidCoords =
		videoRect &&
		Array.isArray(box) &&
		box.length === 4 &&
		box.every((n) => typeof n === "number");

	return (
		<>
			<section className="bg-[#232323] p-4 rounded-lg shadow-md">
				<h2 className="text-xl font-semibold mb-4">Latest Video</h2>

				<div className="flex justify-center relative">
					{playbackId ? (
						<div className="video-wrapper">
							<MuxPlayer
								playbackId={playbackId}
								metadataVideoTitle="Main video to annotate"
								primaryColor="#ffffff"
								secondaryColor="#000000"
								accentColor="#fa50b5"
								playsInline
								controls={false}
								loop={false}
								muted
								style={{ height: "70vh" }}
								className="mx-auto rounded-lg border border-gray-500 aspect-video w-full"
								ref={videoRef}
								onLoadedData={updateRect}
								onEnded={async () => {
									try {
										await completeView({
											variables: { muxPlaybackId: playbackId },
										});
									} catch (e) {
										console.error(e);
									}
									setShowModal(true);
								}}
							/>

							{showBoundingBox && hasValidCoords && (
								<BoundingBox
									location={{
										left: videoRect.left + box[0] * videoRect.width,
										top: videoRect.top + box[1] * videoRect.height,
									}}
									size={{
										width: box[2] * videoRect.width,
										height: box[3] * videoRect.height,
									}}
									message={promptText}
								/>
							)}

							{!isPlaying && (
								<button
									type="button"
									onClick={togglePlayPause}
									className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 rounded-lg"
								>
									<Play size={50} color="white" strokeWidth={3} />
								</button>
							)}
						</div>
					) : (
						<div className="w-full h-[250px] flex items-center justify-center bg-[#1f1f1f] rounded-lg">
							<PawLoader />
						</div>
					)}
				</div>

				<p className="text-gray-400 text-sm mt-2 text-center">
					Tip: Click 'Grab-Bite' every time you see the puppy do this during the
					1-minute video.
				</p>

				<div className="mt-4 flex items-center justify-center space-x-4">
					<CountdownTimer start={isPlaying} />

					<button
						type="button"
						onClick={handleGrabBiteClick}
						className={`border-2 border-[#C6E675] bg-[#2F2D2D] font-bold py-4 px-10 text-lg rounded-xl transition ${
							isGlowing ? "ring-4 ring-[#C6E675] animate-pulse" : ""
						} text-[#C6E675]`}
					>
						Grab-bite
					</button>

					<button
						type="button"
						onClick={() => {
							videoRef.current?.pause();
							setIsPlaying(false);
							setShowTutorial(true);
						}}
						className="p-3 rounded-full flex items-center justify-center bg-[#C6E675] shadow-lg transition hover:scale-105"
					>
						<HelpCircle size={32} color="black" strokeWidth={2.5} />
					</button>
				</div>
			</section>

			{/* now outside of .video-wrapper and <section> */}
			{showTutorial && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80">
					<div className="bg-[#2F2D2D] text-white p-6 rounded-lg shadow-lg max-w-md w-full">
						<h2 className="text-xl font-bold">Grab-bite:</h2>
						<p className="mt-4 text-white text-sm">
							Pup quickly closes jaws around an object or other puppy, without
							chewing or gnawing.
						</p>

						<MuxPlayer
							playbackId="Sf1ZLAykkwuX028cs2v6xe00RVqV7Lg01t8lqVDnB005028A"
							metadataVideoTitle="Help video"
							primaryColor="#ffffff"
							secondaryColor="#000000"
							accentColor="#fa50b5"
							controls={false}
							muted
							autoPlay
							loop
							className="w-full rounded-lg border border-gray-600 mt-4"
						/>

						<button
							type="button"
							onClick={() => {
								setShowTutorial(false);
								setIsPlaying(true);
								videoRef.current?.play();
							}}
							className="mt-4 bg-[#CBDF52] text-black px-4 py-2 rounded-lg w-full"
						>
							Got it!
						</button>
					</div>
				</div>
			)}

			{showModal && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80">
					<div className="bg-[#232323] p-6 rounded-lg text-center shadow-lg w-[500px]">
						<h2 className="text-2xl font-bold text-white mb-2">
							Congratulations! You've just annotated one more video!
						</h2>
						<div className="flex justify-center gap-4">
							<button
								type="button"
								onClick={() => {
									setShowModal(false);
									navigate("/stats");
								}}
								className="px-6 py-3 border border-white text-white rounded-lg text-lg"
							>
								Take a break
							</button>
							<button
								type="button"
								onClick={() => {
									setShowModal(false);
									setIsPlaying(false);
									refetch();
								}}
								className="px-6 py-3 bg-[#C6E675] text-black font-bold rounded-lg text-lg"
							>
								Play next
							</button>
						</div>
					</div>
				</div>
			)}

			{showTimeoutModal && (
				<div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80">
					<div className="bg-[#2F2D2D] text-white p-14 rounded-xl max-w-3xl w-full border border-gray-500 shadow-xl">
						<h2 className="text-3xl font-bold mb-4 text-white">
							Video paused for too long!
						</h2>
						<p className="mb-10 text-xl text-base">
							Since the help box was open for a while, we’ve started a new video
							so you can follow the right puppy from the beginning.
						</p>
						<button
							type="button"
							onClick={() => {
								setShowTimeoutModal(false);
								handlePlayNext();
							}}
							className="bg-[#CBDF52] text-black font-semibold px-6 py-3 rounded-lg text-lg w-full"
						>
							Okay
						</button>
					</div>
				</div>
			)}
		</>
	);
}
